/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useState, useEffect } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import VRMSection from '../VRMSection'
import DVLASection from '../DVLASection'
import OffenceDetailsSection from '../OffenceDetailsSection'
import StayedDetailsSection from '../StayedDetailsSection'

import './VRMDetails.css'
import { ConfirmPopupRendererObject } from '@components/custom/confirmPopupRenderer/ConfirmPopupRenderer'
import { useConfirm } from 'material-ui-confirm'
import { useNavigate } from 'react-router-dom'
import { fetchDataSelector } from '@components/recoil/selectors/selectors'
import { madeChanges, toastMessage } from '@components/recoil/atoms/atoms'
import utils from '@utils/utils'
import { FIELD_IDS, LABELS, ROUTES, MESSAGES } from '../../constants'
import { ManualReviewAPIEndpoints } from '@pages/manualReviewTab/api'
import { fetchLookupDataSelector } from '@components/recoil/selectors/lookupSelectors'

// Re-export for backward compatibility
export const STATIC_IDS = FIELD_IDS

const mapDropdownOptions = (list = [], textField) =>
  (Array.isArray(list) ? list : [])
    ?.map((item) => {
      const text =
        typeof item?.[textField] === 'string' ? item[textField].trim() : ''
      return text ? { id: item?.id ?? text, name: text } : null
    })
    ?.filter(Boolean)

const VRMDetails = ({
  observationDetails = {},
  postDiscardRoute,
  vehicleDetails = {},
  onVehicleDetailsChange = () => {},
}) => {
  const confirmAlert = useConfirm()
  const navigate = useNavigate()
  const getCoreData = useRecoilValue(fetchDataSelector)
  const showToastMessage = useSetRecoilState(toastMessage)
  const setMadeChanges = useSetRecoilState(madeChanges)

  const [vrmValue, setVrmValue] = useState(observationDetails?.vrmNo || '')
  const [isEditing, setIsEditing] = useState(false)
  const getCoreLookupData = useRecoilValue(fetchLookupDataSelector)
  const [vehicleDropdownOptions, setVehicleDropdownOptions] = useState({
    make: [],
    category: [],
    color: [],
  })

  // Extract Offence data from offenceDetails and main observationDetails
  const OFFENCE_API_DATA = {
    referenceId: observationDetails?.id || '',
    siteName: observationDetails?.siteName || '',
    contraventionLocation:
      observationDetails?.offenceDetails?.locationName || '',
    contraventionCode:
      observationDetails?.offenceDetails?.contraventionCode || '',
    locationId: observationDetails?.offenceDetails?.locationId || '',
    cctvSiteId: observationDetails?.offenceDetails?.cctvSiteId || '',
    observationId: observationDetails?.id || '',
    contraventionNote:
      observationDetails?.offenceDetails?.contraventionNote || '',
  }

  // Extract Stayed Details data from observationDetails
  const STAYED_DETAILS_DATA = {
    allowedStayDuration: observationDetails?.allowedStayDuration || '',
    actualStayDuration: observationDetails?.actualStayDuration || '',
    overStayDuration: observationDetails?.overStayDuration || '',
  }

  useEffect(() => {
    if (!isEditing) {
      setVrmValue(observationDetails?.vrmNo || '')
    }
  }, [isEditing, observationDetails?.vrmNo])

  useEffect(() => {
    Promise.all([
      getCoreLookupData(
        ManualReviewAPIEndpoints?.getVehicleMakeList?.method,
        ManualReviewAPIEndpoints?.getVehicleMakeList?.url
      ),
      getCoreData(
        ManualReviewAPIEndpoints?.getVehicleTypeList?.method,
        ManualReviewAPIEndpoints?.getVehicleTypeList?.url,
        null,
        null,
        null,
        true,
        utils?.apiDomainName?.privateLand?.name
      ),
      getCoreLookupData(
        ManualReviewAPIEndpoints?.getVehicleColorList?.method,
        ManualReviewAPIEndpoints?.getVehicleColorList?.url
      ),
    ])
      .then(([makeList, typeList, colorList]) => {
        setVehicleDropdownOptions({
          make:
            makeList?.status === utils?.apiResponseCode?.successCode
              ? mapDropdownOptions(makeList?.data, 'make')
              : [],
          category:
            typeList?.status === utils?.apiResponseCode?.successCode
              ? mapDropdownOptions(typeList?.data, 'vehicleTypeName')
              : [],
          color:
            colorList?.status === utils?.apiResponseCode?.successCode
              ? mapDropdownOptions(colorList?.data, 'color')
              : [],
        })
      })
      .catch((error) => {
        showToastMessage({
          message: error?.response?.data?.message || 'Failed to load DVLA data',
          status: utils?.toastStatus?.error,
        })
      })
  }, [getCoreData, getCoreLookupData, showToastMessage])

  const handleEditClick = () => setIsEditing(true)

  useEffect(() => {
    const hasUnsavedChanges =
      isEditing && observationDetails?.vrmNo !== vrmValue?.trim()

    setMadeChanges(hasUnsavedChanges)

    return () => {
      setMadeChanges(false)
    }
  }, [isEditing, observationDetails?.vrmNo, setMadeChanges, vrmValue])

  const resetEditingState = () => {
    setIsEditing(false)
    setVrmValue(observationDetails?.vrmNo || '') // reset to original API value
    setMadeChanges(false)
  }

  const handleCancelClick = async () => {
    const hasUnsavedChanges = observationDetails?.vrmNo !== vrmValue?.trim()

    if (!hasUnsavedChanges) {
      resetEditingState()
      return
    }

    try {
      await confirmAlert({
        ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
      })
      resetEditingState()
    } catch (error) {}
  }

  const handleUpdateClick = () => {
    getConfirmation('Are you sure want to update this VRM?')
  }

  const handleVrmChange = (e) => {
    setVrmValue(e.target.value)
  }

  const getConfirmation = async () => {
    try {
      await confirmAlert({
        ...ConfirmPopupRendererObject(null, MESSAGES?.vrm?.confirmUpdate),
      })
        .then(() => {
          updateVRM()
        })
        .catch(() => {})
    } catch (error) {
      console.error('Confirmation error:', error)
    }
  }

  const updateVRM = async () => {
    if (!observationDetails?.id) {
      console.error('Observation ID is missing')
      return
    }
    try {
      const endpoint = ManualReviewAPIEndpoints?.updateVRM
      const urlWithParams = endpoint?.url
        .replace('__observationId__', observationDetails?.id)
        .replace('__vrmValue__', vrmValue)

      const response = await getCoreData(
        endpoint?.method,
        urlWithParams,
        null,
        null,
        null,
        true,
        utils?.apiDomainName?.privateLand?.name
      )

      if (response?.status === utils?.apiResponseCode?.successCode) {
        setIsEditing(false)
        setMadeChanges(false)
        showToastMessage({
          message: MESSAGES.vrm.updateSuccess,
          status: 'success',
        })
        navigate(postDiscardRoute || ROUTES.MANUAL_REVIEW)
      } else {
        showToastMessage({
          message: response?.message || MESSAGES?.vrm?.updateFailed,
          status: 'error',
        })
      }
    } catch (error) {
      console.error('Error updating VRM:', error)
      showToastMessage({
        message: error?.response?.data || MESSAGES?.vrm?.updateError,
        status: 'error',
      })
    }
  }

  const checkFuzzyVRM = () => {
    // Implement fuzzy VRM check logic here
    navigate(ROUTES.FUZZY_VRM)
  }

  const handleSaveContraventionCode = (newValue) => {
    // Implement save logic here
    console.log('Saving contravention code:', newValue)
    // API call to save the new contravention code
  }

  const handleDvlaChange = (field, value) => {
    const payloadMap = {
      make: 'vehicleMake',
      color: 'vehicleColor',
      category: 'vehicleType',
    }

    const payloadKey = payloadMap?.[field]
    if (!payloadKey) {
      return
    }

    onVehicleDetailsChange(payloadKey, value)
  }

  const dvlaValues = {
    make: vehicleDetails?.vehicleMake || '',
    category: vehicleDetails?.vehicleType || '',
    color: vehicleDetails?.vehicleColor || '',
  }

  return (
    <>
      <div className="vrm-details-container">
        <VRMSection
          label="VRM"
          vrmValue={vrmValue}
          isEditing={isEditing}
          onEditClick={handleEditClick}
          onCancelClick={handleCancelClick}
          onUpdateClick={handleUpdateClick}
          onVrmChange={handleVrmChange}
          checkFuzzyVRM={checkFuzzyVRM}
          isVRMDisabled={
            observationDetails?.vehicleDetails?.isVRMReviewed === true
          }
          isUpdateDisabled={
            observationDetails?.vehicleDetails?.isVRMReviewed === true ||
            observationDetails?.vrmNo === vrmValue
          }
          observationDetails={observationDetails}
        />
        <DVLASection
          labels={LABELS?.dvla}
          values={dvlaValues}
          options={vehicleDropdownOptions}
          onChange={handleDvlaChange}
          observationDetails={observationDetails}
        />

        <div className="horizontal-separator" aria-hidden="true"></div>

        <OffenceDetailsSection
          labels={LABELS?.offenceDetails}
          values={OFFENCE_API_DATA}
          onSaveContraventionCode={handleSaveContraventionCode}
          observationDetails={observationDetails}
        />

        <div className="horizontal-separator" aria-hidden="true"></div>

        <StayedDetailsSection
          labels={LABELS?.stayedDetails}
          values={STAYED_DETAILS_DATA}
        />
      </div>
    </>
  )
}

export default VRMDetails
